import { useCallback, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

export interface GridDragCreateGhost {
  /** Y px from the top of the column (the lower of start/current). */
  readonly top: number;
  /** Px height of the ghost block. */
  readonly height: number;
  /** Day key the gesture started on. */
  readonly dayKey: string;
}

export interface GridDragCreateCommit {
  readonly dayKey: string;
  /** Hour-fraction of the gesture's start (0–24). */
  readonly startHourFraction: number;
  /** Hour-fraction of the gesture's end. */
  readonly endHourFraction: number;
}

export interface UseGridDragCreateOptions {
  readonly hourHeight: number;
  readonly startHour: number;
  readonly snapMinutes?: number;
  readonly thresholdPx?: number;
  readonly disabled?: boolean;
  /** When true, touch-pointer gestures engage drag-to-create. Default
   *  false — on phones we want native page scrolling instead. Use the
   *  FAB to create events on mobile. */
  readonly enableTouch?: boolean;
  readonly onCommit: (commit: GridDragCreateCommit) => void;
}

export interface UseGridDragCreateResult {
  readonly ghost: GridDragCreateGhost | null;
  readonly bind: {
    readonly onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void;
  };
}

const DEFAULT_SNAP = 30;
const DEFAULT_THRESHOLD = 4;

/**
 * Pointer-events drag-to-create hook for the timed grid. Attaches to
 * each day column. On primary-button pointerdown that misses any
 * existing event block, the hook starts tracking; on pointerup it
 * fires `onCommit({ dayKey, startHourFraction, endHourFraction })`.
 *
 * The view is responsible for finding the column the pointer started in
 * and rendering the visual ghost from `result.ghost`.
 *
 * Caller wires the column with:
 * ```tsx
 * <div data-day-key={key} onPointerDown={...} />
 * ```
 *
 * Existing event blocks should `e.stopPropagation()` on their own
 * pointerdown so they don't accidentally trigger drag-to-create.
 */
export function useGridDragCreate(
  options: UseGridDragCreateOptions,
): UseGridDragCreateResult {
  const {
    hourHeight,
    startHour,
    snapMinutes = DEFAULT_SNAP,
    thresholdPx = DEFAULT_THRESHOLD,
    disabled = false,
    enableTouch = false,
    onCommit,
  } = options;

  const [ghost, setGhost] = useState<GridDragCreateGhost | null>(null);

  const startRef = useRef<{
    columnEl: HTMLElement;
    columnTop: number;
    columnHeight: number;
    dayKey: string;
    startY: number;
    startClientY: number;
    engaged: boolean;
  } | null>(null);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLElement>): void => {
      if (disabled) return;
      if (e.button !== 0) return;
      // Touch-pointer gestures bail to preserve native scroll. The FAB
      // is the mobile create path.
      if (e.pointerType === 'touch' && !enableTouch) return;
      const column = e.currentTarget;
      const dayKey = column.dataset.dayKey;
      if (dayKey === undefined) return;
      const rect = column.getBoundingClientRect();
      const startY = e.clientY - rect.top;
      startRef.current = {
        columnEl: column,
        columnTop: rect.top,
        columnHeight: rect.height,
        dayKey,
        startY,
        startClientY: e.clientY,
        engaged: false,
      };
      try {
        column.setPointerCapture(e.pointerId);
      } catch {
        // ignore
      }

      const minutesPerPx = 60 / hourHeight;

      const handleMove = (ev: PointerEvent): void => {
        const start = startRef.current;
        if (start === null) return;
        const dy = ev.clientY - start.startClientY;
        if (!start.engaged && Math.abs(dy) < thresholdPx) return;
        start.engaged = true;
        // Clamp the live cursor inside the column so dragging past the
        // top/bottom edge doesn't grow the ghost off-grid.
        const currentY = Math.max(
          0,
          Math.min(start.columnHeight, ev.clientY - start.columnTop),
        );
        const top = Math.min(start.startY, currentY);
        const height = Math.max(8, Math.abs(currentY - start.startY));
        // Snap visually to the chosen minute granularity.
        const snapPx = (snapMinutes / 60) * hourHeight;
        const snappedTop = Math.max(0, Math.round(top / snapPx) * snapPx);
        const maxHeight = start.columnHeight - snappedTop;
        const snappedHeight = Math.min(
          maxHeight,
          Math.max(snapPx, Math.round(height / snapPx) * snapPx),
        );
        setGhost({ top: snappedTop, height: snappedHeight, dayKey: start.dayKey });
      };

      const handleUp = (ev: PointerEvent): void => {
        const start = startRef.current;
        document.removeEventListener('pointermove', handleMove);
        document.removeEventListener('pointerup', handleUp);
        document.removeEventListener('pointercancel', handleUp);
        startRef.current = null;
        if (start === null) {
          setGhost(null);
          return;
        }
        const wasEngaged = start.engaged;
        setGhost(null);
        if (!wasEngaged) return;

        const dy = ev.clientY - start.startClientY;
        const startMinutesRaw = start.startY * minutesPerPx;
        const endMinutesRaw = (start.startY + dy) * minutesPerPx;
        const startMinutes = Math.round(startMinutesRaw / snapMinutes) * snapMinutes;
        const endMinutes = Math.round(endMinutesRaw / snapMinutes) * snapMinutes;
        // Clamp to the column's visible range so a drag that overshoots
        // the bottom doesn't emit an end past `endHour + 1`.
        const maxFraction = startHour + start.columnHeight / hourHeight;
        let startHourFraction = startHour + Math.min(startMinutes, endMinutes) / 60;
        let endHourFraction = startHour + Math.max(startMinutes, endMinutes) / 60;
        startHourFraction = Math.max(startHour, Math.min(startHourFraction, maxFraction));
        endHourFraction = Math.max(startHour, Math.min(endHourFraction, maxFraction));
        // Floor at start + snap; if the snap floor would push past
        // maxFraction, shift the start back instead so the event stays
        // in the column.
        if (endHourFraction <= startHourFraction) {
          const snapFraction = snapMinutes / 60;
          if (startHourFraction + snapFraction <= maxFraction) {
            endHourFraction = startHourFraction + snapFraction;
          } else {
            endHourFraction = maxFraction;
            startHourFraction = Math.max(startHour, maxFraction - snapFraction);
          }
        }
        onCommit({
          dayKey: start.dayKey,
          startHourFraction,
          endHourFraction,
        });
      };

      document.addEventListener('pointermove', handleMove);
      document.addEventListener('pointerup', handleUp);
      document.addEventListener('pointercancel', handleUp);
    },
    [disabled, hourHeight, snapMinutes, startHour, thresholdPx, enableTouch, onCommit],
  );

  return {
    ghost,
    bind: { onPointerDown },
  };
}
